import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type ChangeEvent } from 'react';
import { Link } from 'react-router';
import { api, FalhaDaApi } from '../api/cliente';
import {
  Alerta,
  Botao,
  CabecalhoDaPagina,
  Cartao,
  Ficha,
  TituloDeSecao,
} from '../componentes/base';
import { Caixa, Seletor } from '../componentes/campos';
import { data, documento, moeda, plural, telefone } from '../util/formato';

interface LinhaAnalisada {
  readonly numeroDaLinha: number;
  readonly nome: string;
  readonly documento: string | null;
  readonly documentoValido: boolean;
  readonly telefone: string | null;
  readonly valorOriginal: number;
  readonly vencimentoOriginal: string | null;
  readonly avisos: string[];
  readonly erros: string[];
  readonly ehDuplicata: boolean;
}

interface Analise {
  readonly clinica: { id: string; nome: string };
  readonly totalDeLinhas: number;
  readonly colunas: Array<{
    indice: number;
    cabecalho: string;
    campo: string | null;
    correspondencia: string;
  }>;
  readonly camposObrigatoriosFaltando: string[];
  readonly podeImportar: boolean;
  readonly resumo: { prontas: number; duplicadas: number; comErro: number; comAviso: number };
  readonly amostra: LinhaAnalisada[];
  readonly linhas: LinhaAnalisada[];
}

export function PaginaImportar() {
  const [clinicaId, setClinicaId] = useState('');
  const [analise, setAnalise] = useState<Analise | null>(null);
  const [ignorarDuplicadas, setIgnorarDuplicadas] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [concluido, setConcluido] = useState<{ importados: number; ignorados: number } | null>(
    null,
  );

  const { data: clinicas } = useQuery({
    queryKey: ['clinicas', 'selecao'],
    queryFn: () => api.buscar<Array<{ id: string; nome: string }>>('/clinicas/selecao'),
  });

  const analisar = useMutation({
    mutationFn: ({ nomeArquivo, base64 }: { nomeArquivo: string; base64: string }) =>
      api.criar<Analise>('/importacao/analisar', { clinicaId, nomeArquivo, base64 }),
    onSuccess: (r) => {
      setErro(null);
      setAnalise(r);
    },
    onError: (e) => setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível ler o arquivo.'),
  });

  const confirmar = useMutation({
    mutationFn: () =>
      api.criar<{ importados: number; ignorados: number }>('/importacao/confirmar', {
        clinicaId,
        linhas: analise?.linhas ?? [],
        ignorarDuplicadas,
      }),
    onSuccess: (r) => {
      setConcluido(r);
      setAnalise(null);
    },
    onError: (e) => setErro(e instanceof FalhaDaApi ? e.message : 'Falha ao importar.'),
  });

  function selecionarArquivo(evento: ChangeEvent<HTMLInputElement>) {
    const arquivo = evento.target.files?.[0];
    if (!arquivo || !clinicaId) return;

    const leitor = new FileReader();
    leitor.onload = () =>
      analisar.mutate({ nomeArquivo: arquivo.name, base64: String(leitor.result) });
    leitor.readAsDataURL(arquivo);
  }

  return (
    <>
      <CabecalhoDaPagina
        titulo="Importar planilha"
        descricao="Traga a relação de devedores enviada pela clínica"
        acoes={
          <a
            href="/api/importacao/modelo.csv"
            className="rounded-md border px-3.5 py-2 text-sm font-medium text-[color:var(--texto)] hover:border-[color:var(--acento)] hover:text-[color:var(--acento)]"
          >
            Baixar planilha modelo
          </a>
        }
      />

      {erro && (
        <div className="mb-5">
          <Alerta tom="vencido">{erro}</Alerta>
        </div>
      )}

      {concluido && (
        <div className="mb-5">
          <Alerta tom="recebido">
            {plural(concluido.importados, 'devedor importado', 'devedores importados')}
            {concluido.ignorados > 0 && `, ${concluido.ignorados} linha(s) ignorada(s)`}.{' '}
            <Link
              to="/devedores"
              className="font-medium underline underline-offset-2"
              style={{ color: 'var(--acento-sobre-superficie)' }}
            >
              Ver a carteira →
            </Link>
          </Alerta>
        </div>
      )}

      {!analise ? (
        <Cartao className="max-w-2xl">
          <TituloDeSecao auxiliar="etapa 1 de 2">Escolher a planilha</TituloDeSecao>

          <div className="mb-4">
            <Seletor
              rotulo="Clínica credora"
              valor={clinicaId}
              aoMudar={setClinicaId}
              vazio="Selecione a clínica"
              opcoes={(clinicas ?? []).map((c) => ({ valor: c.id, rotulo: c.nome }))}
            />
          </div>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-[color:var(--texto-suave)]">
              Arquivo CSV
            </span>
            <input
              type="file"
              accept=".csv,.txt"
              disabled={!clinicaId || analisar.isPending}
              onChange={selecionarArquivo}
              className="w-full rounded-md border bg-[color:var(--superficie-elevada)] px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-[color:var(--superficie-afundada)] file:px-3 file:py-1.5 file:text-sm file:text-[color:var(--texto)] disabled:opacity-50"
            />
          </label>

          <p className="mt-3 text-xs text-[color:var(--texto-tenue)]">
            No Excel, use "Salvar como" e escolha <strong>CSV UTF-8</strong>. Nada é gravado
            nesta etapa — você confere tudo antes.
          </p>

          {analisar.isPending && (
            <p className="mt-3 text-sm text-[color:var(--texto-suave)]">Lendo a planilha…</p>
          )}
        </Cartao>
      ) : (
        <>
          <Cartao className="mb-5">
            <TituloDeSecao auxiliar="etapa 2 de 2">
              Confira antes de gravar — {analise.clinica.nome}
            </TituloDeSecao>

            <div className="mb-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
              <span>
                <strong className="tabular">{analise.resumo.prontas}</strong>{' '}
                <span className="text-[color:var(--texto-suave)]">prontas para importar</span>
              </span>
              {analise.resumo.duplicadas > 0 && (
                <span style={{ color: 'var(--color-atencao)' }}>
                  <strong className="tabular">{analise.resumo.duplicadas}</strong> já constam na
                  carteira
                </span>
              )}
              {analise.resumo.comAviso > 0 && (
                <span className="text-[color:var(--texto-suave)]">
                  <strong className="tabular">{analise.resumo.comAviso}</strong> com aviso
                </span>
              )}
              {analise.resumo.comErro > 0 && (
                <span style={{ color: 'var(--color-vencido)' }}>
                  <strong className="tabular">{analise.resumo.comErro}</strong> com erro — serão
                  puladas
                </span>
              )}
            </div>

            {/* O mapeamento reconhecido fica à vista: é o ponto em que um erro passa
                despercebido e 300 devedores entram com o valor no campo do CPF. */}
            <div className="mb-4">
              <p className="mb-2 text-xs font-medium text-[color:var(--texto-suave)]">
                Colunas reconhecidas
              </p>
              <div className="flex flex-wrap gap-1.5">
                {analise.colunas.map((c) => (
                  <span
                    key={c.indice}
                    className="rounded border px-2 py-1 text-xs"
                    style={
                      c.campo
                        ? undefined
                        : { color: 'var(--texto-tenue)', borderStyle: 'dashed' }
                    }
                  >
                    <span className="text-[color:var(--texto-tenue)]">{c.cabecalho}</span>
                    {c.campo ? (
                      <>
                        <span className="mx-1">→</span>
                        <strong style={{ color: 'var(--acento)' }}>{c.campo}</strong>
                      </>
                    ) : (
                      <span className="ml-1">ignorada</span>
                    )}
                  </span>
                ))}
              </div>
            </div>

            {analise.camposObrigatoriosFaltando.length > 0 && (
              <div className="mb-4">
                <Alerta tom="vencido">
                  Não encontrei as colunas obrigatórias:{' '}
                  <strong>{analise.camposObrigatoriosFaltando.join(', ')}</strong>. Confira
                  se a primeira linha da planilha tem os nomes das colunas.
                </Alerta>
              </div>
            )}

            <div className="mb-4">
              <Caixa
                rotulo="Pular quem já está na carteira desta clínica"
                marcado={ignorarDuplicadas}
                aoMudar={setIgnorarDuplicadas}
              />
            </div>

            <div className="flex gap-2">
              <Botao onClick={() => setAnalise(null)}>Escolher outro arquivo</Botao>
              <Botao
                tipo="primario"
                onClick={() => confirmar.mutate()}
                desabilitado={!analise.podeImportar || confirmar.isPending}
              >
                {confirmar.isPending
                  ? 'Importando…'
                  : `Importar ${analise.resumo.prontas} devedores`}
              </Botao>
            </div>
          </Cartao>

          <Cartao semPadding>
            <div className="p-5 pb-3">
              <TituloDeSecao auxiliar={`primeiras ${analise.amostra.length} de ${analise.totalDeLinhas}`}>
                Amostra interpretada
              </TituloDeSecao>
            </div>
            <div className="rolagem-lateral border-t">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-[color:var(--superficie-afundada)] text-left text-[0.6875rem] uppercase tracking-[0.08em] text-[color:var(--texto-tenue)]">
                    <th className="px-5 py-2 font-medium">Linha</th>
                    <th className="px-3 py-2 font-medium">Nome</th>
                    <th className="px-3 py-2 font-medium">CPF</th>
                    <th className="px-3 py-2 font-medium">Telefone</th>
                    <th className="px-3 py-2 text-right font-medium">Valor</th>
                    <th className="px-3 py-2 font-medium">Vencimento</th>
                    <th className="px-5 py-2 font-medium">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {analise.amostra.map((linha) => (
                    <tr key={linha.numeroDaLinha} className="border-b last:border-0">
                      <td className="tabular px-5 py-2.5 text-[color:var(--texto-tenue)]">
                        {linha.numeroDaLinha}
                      </td>
                      <td className="px-3 py-2.5">{linha.nome || '—'}</td>
                      <td className="px-3 py-2.5 font-mono text-xs">
                        {documento(linha.documento)}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs">
                        {telefone(linha.telefone)}
                      </td>
                      <td className="tabular px-3 py-2.5 text-right whitespace-nowrap">
                        {moeda(linha.valorOriginal)}
                      </td>
                      <td className="tabular px-3 py-2.5 whitespace-nowrap">
                        {data(linha.vencimentoOriginal)}
                      </td>
                      <td className="px-5 py-2.5">
                        {linha.erros.length > 0 ? (
                          <Ficha tom="vencido">{linha.erros[0]}</Ficha>
                        ) : linha.ehDuplicata ? (
                          <Ficha tom="atencao">já na carteira</Ficha>
                        ) : linha.avisos.length > 0 ? (
                          <span
                            className="text-xs"
                            style={{ color: 'var(--color-atencao)' }}
                            title={linha.avisos.join(' · ')}
                          >
                            {linha.avisos[0]}
                          </span>
                        ) : (
                          <Ficha tom="recebido">pronta</Ficha>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Cartao>
        </>
      )}
    </>
  );
}
